import React, { useState, useEffect, useRef } from "react";
import { clsx } from "clsx";
import { EnvelopeIcon } from "@heroicons/react/20/solid";
import {
	CarIcon,
	SailboatIcon,
	MopedFrontIcon,
	VanIcon,
	IslandIcon,
	TractorIcon,
	TruckTrailerIcon,
	SuitcaseIcon,
} from "@phosphor-icons/react";
import {
	LOAN_PURPOSE_PERSONAL,
	LOAN_TYPE_BIKE,
	LOAN_TYPE_BOAT,
	LOAN_TYPE_BUSINESS,
	LOAN_TYPE_CAR,
	LOAN_TYPE_CARAVAN,
	LOAN_TYPE_EQUIPMENT,
	LOAN_TYPE_MOTORCYCLE,
	LOAN_TYPE_PERSONAL,
	LOAN_TYPE_TRUCK,
	loanFieldsMap,
	emailTypos,
} from "@/lib/constants";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { RadioGroupField } from "@/components/ui/radioGroupField";
import ToggleSwitch from "@/components/ui/toggleSwitch";
import { secureInput } from "@/lib/security";

interface Step1Props {
	enabled: boolean;
	setEnabled: React.Dispatch<React.SetStateAction<boolean>>;
	loanType?: string;
}

export const PersonalDetails: React.FC<Step1Props> = ({ enabled, setEnabled, loanType }) => {
	// Get values and setters from Zustand store
	const { formValues, setFormValue, setFormError, formErrors } = useFormDataStore();
	const {
		selectedIndex,
		loanAmountMin,
		setFormErrors,
		enabledFields,
		setEnabledFields,
		enabledLoans,
		setValidateFormDetails,
		formContainer,
		toggleTC,
		setToggleTC,
		hasAttemptedSubmit,
	} = useFormStateStore();

	// Reset form errors on focus
	const handleFocus = (field: keyof typeof errors) => {
		setErrors((prev) => ({ ...prev, [field]: false }));
	};

	// Toggle Switch for T&C
	const [enabledTC, setEnabledTC] = useState(toggleTC);

	useEffect(() => {
		setToggleTC(enabledTC);
		if (errors.tc) {
			setErrors((prev) => ({ ...prev, tc: !enabledTC }));
		}
	}, [enabledTC]);

	const { firstName, lastName, email, loanAmount, loanType: zLoanType } = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		firstName: false,
		lastName: false,
		email: false,
		loanType: false,
		loanAmount: false,
		tc: false,
	});

	const [isNavDivSmall, setIsNavDivSmall] = useState(false);

	// For form container
	useEffect(() => {
		const limiter = 600;

		setIsNavDivSmall(formContainer < limiter);
	}, [selectedIndex, formContainer, isNavDivSmall]);

	// Email Typo Validation Helper
	const getEmailValidationError = (emailValue: string): string => {
		const trimmed = emailValue.trim();
		if (trimmed === "") return "Email is required";

		const sanitizedValue = secureInput.email(trimmed);
		if (!sanitizedValue.isValid) return "Email is not correctly formatted";

		const fullValue = trimmed.toLowerCase();
		const parts = fullValue.split("@");

		if (parts.length === 2) {
			const [username, domain] = parts;
			const typoRegex = /\.(con\.au|cm\.au|con|cm|co)$/i;
			const endTypoRegex = /\.com[a-z]$/;

			if (emailTypos[domain]) {
				return `Did you mean ${username}@${emailTypos[domain]}?`;
			} else if (typoRegex.test(domain)) {
				const fixedDomain = domain.replace(typoRegex, (match) => {
					return match.includes(".au") ? ".com.au" : ".com";
				});
				return `Did you mean ${username}@${fixedDomain}?`;
			} else if (endTypoRegex.test(domain)) {
				const fixedDomain = domain.replace(endTypoRegex, (match) => {
					return match.includes(".au") ? ".com.au" : ".com";
				});
				return `Did you mean ${username}@${fixedDomain}?`;
			}
		}
		return "";
	};

	// Handle input changes with validation
	const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const sanitizedValue = secureInput.name(value);
		setFormValue("firstName", sanitizedValue.sanitized);
		setErrors((prev) => ({ ...prev, firstName: !sanitizedValue.isValid }));
	};

	const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const sanitizedValue = secureInput.name(value);
		setFormValue("lastName", sanitizedValue.sanitized);
		setErrors((prev) => ({ ...prev, lastName: !sanitizedValue.isValid }));
	};

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const sanitizedValue = secureInput.email(value);
		setFormValue("email", sanitizedValue.sanitized);

		const emailErrorMsg = getEmailValidationError(sanitizedValue.sanitized);
		setErrors((prev) => ({ ...prev, email: emailErrorMsg !== "" }));
		setFormError("email", emailErrorMsg);
	};

	const handleLoanAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let formattedValue = +e.currentTarget.value.replace(/[^0-9]/g, "");
		setFormValue("loanAmount", formattedValue.toLocaleString());
		setErrors((prev) => ({
			...prev,
			loanAmount: formattedValue < loanAmountMin,
		}));
	};

	const handleLoanTypeChange = (value: string) => {
		setFormValue("loanType", value);
		setErrors((prev) => ({ ...prev, loanType: !value }));
		if (value) {
			setEnabledFields(value);
		}
		if (
			//86cyywnev
			loanFieldsMap[value]?.loanPurpose[0].value === LOAN_PURPOSE_PERSONAL ||
			(loanFieldsMap[value]?.loanPurpose.length == 2 &&
				loanFieldsMap[value]?.loanPurpose[1].value === LOAN_PURPOSE_PERSONAL)
		) {
			setFormValue("abnStatus", "");
			setFormValue("gstStatus", "");
		}
	};

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormErrors(hasError);
	}, [errors, setFormErrors]);

	// Function to validate all fields
	const validateForm = () => {
		const emailErrorMsg = getEmailValidationError(email);

		const newErrors = {
			firstName: !secureInput.name(firstName).isValid,
			lastName: !secureInput.name(lastName).isValid,
			email: emailErrorMsg !== "",
			loanAmount: !loanAmount || parseFloat(loanAmount.replace(/,/g, "")) < loanAmountMin,
			loanType: !loanType,
			tc: !enabledTC,
		};

		setErrors(newErrors);
		setFormError("email", emailErrorMsg);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormErrors(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [firstName, lastName, email, loanAmount, zLoanType, loanType, enabledTC]);

	return (
		<div className="eL-space-y-3">
			<div>
				<label
					htmlFor="firstName"
					className="eL-text-base eL-font-semibold eL-leading-6 eL-text-gray-900"
				>
					Contact Details
				</label>
				<div
					className={clsx(
						isNavDivSmall
							? "eL-flex-col eL-gap-x-2 eL-gap-y-1"
							: "eL-flex-row eL-gap-x-2",
						"eL-flex",
					)}
				>
					<div className="eL-flex-1">
						<div className="eL-relative eL-mt-2">
							<input
								name="firstName"
								value={firstName}
								onChange={handleFirstNameChange}
								onFocus={() => handleFocus("firstName")}
								aria-label="First Name"
								type="text"
								id="firstName"
								placeholder="First Name"
								maxLength={30}
								autoFocus
								autoComplete="given-name"
								className={clsx(
									hasAttemptedSubmit && errors.firstName
										? "eL-ring-red-500 focus:eL-ring-red-500"
										: "eL-ring-gray-300 focus:eL-ring-[--eL-theme-color]",
									"eL-block eL-w-full eL-rounded-md eL-shadow-sm eL-border-0 eL-py-2.5 eL-text-gray-900 eL-ring-1 eL-outline-1 eL-outline-gray-900 eL-ring-inset focus:eL-ring-1 focus:eL-ring-inset focus-visible:eL-outline-[--eL-theme-color] eL-pl-3 placeholder:eL-text-gray-400 sm:eL-text-md sm:eL-leading-6",
								)}
							/>
						</div>
						{hasAttemptedSubmit && errors.firstName && (
							<span className="eL-text-xs eL-text-red-500">First name required</span>
						)}
					</div>
					<div className="eL-flex-1">
						<div className="eL-relative eL-mt-2">
							<input
								name="lastName"
								value={lastName}
								onChange={handleLastNameChange}
								onFocus={() => handleFocus("lastName")}
								aria-label="Last Name"
								type="text"
								id="lastName"
								placeholder="Last Name"
								maxLength={30}
								autoComplete="family-name"
								className={clsx(
									hasAttemptedSubmit && errors.lastName
										? "eL-ring-red-500 focus:eL-ring-red-500"
										: "eL-ring-gray-300 focus:eL-ring-[--eL-theme-color]",
									"eL-block eL-w-full eL-rounded-md eL-shadow-sm eL-border-0 eL-py-2.5 eL-text-gray-900 eL-ring-1 eL-outline-1 eL-outline-gray-900 eL-ring-inset focus:eL-ring-1 focus:eL-ring-inset focus-visible:eL-outline-[--eL-theme-color] eL-pl-3 placeholder:eL-text-gray-400 sm:eL-text-md sm:eL-leading-6",
								)}
							/>
						</div>
						{hasAttemptedSubmit && errors.lastName && (
							<span className="eL-text-xs eL-text-red-500">
								Last name is required
							</span>
						)}
					</div>
				</div>

				<div className="eL-relative eL-mt-2">
					<div className="eL-pointer-events-none eL-absolute eL-inset-y-0 eL-left-0 eL-flex eL-items-center eL-pl-3">
						<EnvelopeIcon
							className="eL-h-5 eL-w-5 eL-text-gray-400"
							aria-hidden="true"
						/>
					</div>
					<input
						value={email}
						onChange={handleEmailChange}
						onFocus={() => {
							handleFocus("email");
							setFormError("email", "");
						}}
						aria-label="Email Address"
						type="email"
						id="email"
						placeholder="Email Address"
						maxLength={50}
						autoComplete="email"
						className={clsx(
							(hasAttemptedSubmit && errors.email) || formErrors.email
								? "eL-ring-red-500 focus:eL-ring-red-500"
								: "eL-ring-gray-300 focus:eL-ring-[--eL-theme-color]",
							"eL-block eL-w-full eL-rounded-md eL-shadow-sm eL-border-0 eL-py-2.5 eL-pl-10 eL-text-gray-900 eL-ring-1 eL-outline-1 eL-outline-gray-900 eL-ring-inset focus:eL-ring-1 focus:eL-ring-inset focus-visible:eL-outline-[--eL-theme-color] placeholder:eL-text-gray-400 sm:eL-text-md sm:eL-leading-6 eL-bg-none",
						)}
					/>
				</div>
				{formErrors.email && (
					<span className="eL-text-xs eL-text-red-500">{formErrors.email}</span>
				)}
			</div>
			{enabledLoans.length > 1 && (
				<RadioGroupField
					id="loanType"
					label="Choose a loan type"
					value={loanType || ""}
					options={enabledLoans.map((item) => ({
						id: item.id,
						value: item.value,
						label: item.value,
						icon:
							item.value === LOAN_TYPE_CAR ? (
								<CarIcon size={24} weight="duotone" />
							) : item.value === LOAN_TYPE_BOAT ? (
								<SailboatIcon size={24} weight="duotone" />
							) : item.value === LOAN_TYPE_MOTORCYCLE ||
							  item.value === LOAN_TYPE_BIKE ? (
								<MopedFrontIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_CARAVAN ? (
								<VanIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_PERSONAL ? (
								<IslandIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_EQUIPMENT ? (
								<TractorIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_TRUCK ? (
								<TruckTrailerIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_BUSINESS ? (
								<SuitcaseIcon size={24} weight="duotone" />
							) : null,
					}))}
					onChange={(value) => {
						handleLoanTypeChange(value);
					}}
					required={true}
					error={errors.loanType ? "This field is required" : ""}
					disabled={false}
					className={""}
					gridCols={4}
					showIcons={true}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			)}

			<div
				className={clsx(
					isNavDivSmall
						? "eL-flex-col eL-gap-x-2  eL-space-y-2"
						: "eL-flex-row eL-gap-x-2",
					"eL-flex",
				)}
			>
				<div className="eL-flex-1">
					<label
						htmlFor="loan-amount"
						className="eL-text-base eL-font-semibold eL-leading-6 eL-text-gray-900"
					>
						Loan Amount
					</label>
					<div className="eL-relative eL-mt-2">
						<div className="eL-pointer-events-none eL-absolute eL-inset-y-0 eL-left-0 eL-flex eL-items-center eL-pl-3">
							<span className="eL-text-gray-500 sm:eL-text-md">$</span>
						</div>
						<input
							value={loanAmount}
							onChange={handleLoanAmountChange}
							onFocus={() => handleFocus("loanAmount")}
							aria-label="Loan Amount"
							placeholder={
								loanAmountMin >= 10000
									? `Loan Amount (Min 10K)`
									: `Loan Amount (Min 5K)`
							}
							maxLength={10}
							className={clsx(
								hasAttemptedSubmit && errors.loanAmount
									? "eL-ring-red-500 focus:eL-ring-red-500"
									: "eL-ring-gray-300 focus:eL-ring-[--eL-theme-color]",
								"eL-block eL-w-full eL-rounded-md eL-shadow-sm eL-border-0 eL-py-2.5 eL-pl-7 eL-pr-12 eL-text-gray-900 eL-ring-1 eL-ring-inset eL-ring-gray-300 focus:eL-ring-1 eL-outline-1 focus:eL-ring-inset focus-visible:eL-outline-[--eL-theme-color] placeholder:eL-text-gray-400 sm:eL-text-md sm:eL-leading-6",
							)}
							type="text"
							id="loan-amount"
							aria-describedby="loan-amount"
							inputMode="numeric"
						/>
						<div className="eL-pointer-events-none eL-absolute eL-inset-y-0 eL-right-0 eL-flex eL-items-center eL-pr-3">
							<span className="eL-text-gray-500 sm:eL-text-md">AUD</span>
						</div>
					</div>
					{hasAttemptedSubmit && errors.loanAmount && (
						<span className="eL-text-xs eL-text-red-500">
							Minimum loan amount is {loanAmountMin >= 10000 ? `$10,000` : `$5,000`}
						</span>
					)}
				</div>
			</div>

			<div id="tc-toggle" className={clsx("!eL-mt-4")} tabIndex={-1}>
				<ToggleSwitch label="" enabled={enabledTC} setEnabled={setEnabledTC} />
				{hasAttemptedSubmit && errors.tc && (
					<span className="eL-block eL-mt-1 eL-text-xs eL-text-red-500">
						Please agree to the terms and conditions to continue
					</span>
				)}
			</div>
		</div>
	);
};
